/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Scale, Cookie, FileCode, Mail, Copyright } from 'lucide-react';
import { LEGAL_OWNER } from '@/lib/legalOwner';
import { IP_FOOTER_NOTICE } from '@/config/ipProtection';

/**
 * LegalFooter — global footer rendered on every page after the main content.
 *
 * Prints the Data Controller identity + VAT exemption notice + a compact
 * links row. Auto-generated from `lib/legalOwner.js` so a single edit
 * propagates to invoices, PDF exports and this footer.
 */
export const LegalFooter = ({ compact = false }) => {
  const year = new Date().getFullYear();
  return (
    <footer
      className="border-t border-border/60 bg-muted/30 mt-auto"
      data-testid="global-legal-footer"
    >
      <div className={`max-w-6xl mx-auto px-4 ${compact ? 'py-3' : 'py-5'} space-y-2`}>
        {!compact && (
          <div className="grid md:grid-cols-[1fr_auto] gap-3 items-start">
            <div className="text-[11px] text-muted-foreground leading-relaxed" data-testid="footer-legal-block">
              <div className="font-semibold text-foreground mb-0.5">
                Titolare del software SanitàNexus
              </div>
              <div>
                <span className="font-medium text-foreground">{LEGAL_OWNER.fullName}</span>
                {' '}· CF <span className="font-mono">{LEGAL_OWNER.fiscalCode}</span>
              </div>
              <div>{LEGAL_OWNER.addressLine}</div>
              <div className="italic mt-0.5">{LEGAL_OWNER.vatExemptionNotice}</div>
            </div>
            <a
              href={`mailto:${LEGAL_OWNER.contactEmail}`}
              className="inline-flex items-center gap-1.5 text-[11px] text-primary hover:underline"
              data-testid="footer-contact-email"
            >
              <Mail className="h-3 w-3" /> {LEGAL_OWNER.contactEmail}
            </a>
          </div>
        )}
        <div
          className="flex items-start gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-[11px] leading-relaxed text-foreground/90"
          data-testid="footer-ip-notice"
        >
          <Copyright className="h-3.5 w-3.5 mt-0.5 text-primary shrink-0" aria-hidden="true" />
          <span className="font-medium">{IP_FOOTER_NOTICE}</span>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-border/40">
          <div className="text-[11px] text-muted-foreground">
            © {year} SanitàNexus · Software distribuito ai sensi della Legge 633/1941
          </div>
          <nav className="flex items-center gap-3 text-[11px]" aria-label="Legal">
            <Link to="/legal/privacy" className="inline-flex items-center gap-1 text-primary hover:underline" data-testid="footer-link-privacy">
              <ShieldCheck className="h-3 w-3" /> Privacy
            </Link>
            <Link to="/legal/terms" className="inline-flex items-center gap-1 text-primary hover:underline" data-testid="footer-link-terms">
              <FileCode className="h-3 w-3" /> Terms
            </Link>
            <Link to="/legal/cookie" className="inline-flex items-center gap-1 text-primary hover:underline" data-testid="footer-link-cookie">
              <Cookie className="h-3 w-3" /> Cookie
            </Link>
            <Link to="/legal/dpa" className="inline-flex items-center gap-1 text-primary hover:underline" data-testid="footer-link-dpa">
              <Scale className="h-3 w-3" /> DPA
            </Link>
            <Link to="/legal/license" className="inline-flex items-center gap-1 text-primary hover:underline" data-testid="footer-link-license">
              <Copyright className="h-3 w-3" /> Licenza
            </Link>
          </nav>
        </div>
      </div>
    </footer>
  );
};

export default LegalFooter;
